/** @typedef {Object} alkModalService
 * @property {Function} show
 * @property {Function} initiateModalWizard
 * @property {Function} openNextModal
 * @property {Function} openPrevModal
 * @property {Function} getCurrentWizardData
 * @property {Function} setCurrentWizardData
 * @property {Function} clearWizardData
 * @property {Function} setWizardCleanupFunction
 */

(function (angular) {

  angular
    .module("alk.modal")
    .factory("alkModalService", ["$uibModal", "$translate",
      /** @name alkModalService
       * @ngdoc service
       * @param {service} $uibModal angularJS twitter bootstrap [modal service](https://angular-ui.github.io/bootstrap/#!#modal)
       * @param {service} $translate angularJS [translation service](https://angular-translate.github.io/)
       * @description Provides services for displaying  and configuring custom modal flows.
       * @returns alkModalService
       * @memberof alk.modal
       */
      function ($uibModal, $translate) {

        var defaultController = ["modalOptions", "$uibModalInstance", function defaultModalController(modalOptions, $uibModalInstance) {
          let vm = this;
          vm.modalOptions = modalOptions;

          vm.close = function () {
            $uibModalInstance.close(modalOptions);
          }

          vm.dismiss = function () {
            $uibModalInstance.dismiss("cancel");
          }
        }];

        var modalWizardArray = [];
        var modalWizardIndex = 0;
        var currentWizardData = null;
        var wizardCleanupFunction = null;

        var modalDefaults = {
          backdrop: true,
          keyboard: true,
          modalFade: true,
          template: require("./modal_dialog.html"),
        };

        function getModalOptions() {
          return {
            closeButtonText: $translate.instant("views.common.Cancel"),
            actionButtonText: $translate.instant("views.common.OK"),
            okOnly: false,
            headerText: "",
            bodyText: null,
            bodyTemplate: null,
            data: {}
          };
        }

        return {
          show: show,
          initiateModalWizard: initiateModalWizard,
          openNextModal: openNextModal,
          openPrevModal: openPrevModal,
          getCurrentWizardData: getCurrentWizardData,
          setCurrentWizardData: setCurrentWizardData,
          clearWizardData: clearWizardData,
          setWizardCleanupFunction: setWizardCleanupFunction
        };

        /** show - Display a modal. The default is to display a "static" modal according to ui-bootstrap.
         * @param {Object} customModalDefaults - Custom defaults for the modal. The same options for a [uibootstrap-modal](https://angular-ui.github.io/bootstrap/#!#modal)
         * are allowed here.
         * @param {Object} customModalOptions - Custom options for the modal. The custom options object can look like
         * `{bodyTemplate: string, bodyText: string, headerText: string, okOnly: boolean, closeButtonText: string, actionButtonText: string, onRender: function}`.
         * Reasonable defaults for an actionable modal are to supply just `bodyText` and `headerText`.
         *
         * `bodyTemplate` is a url to template to include in the modal's body.
         *
         * `bodyText` is a string of text that will be displayed in the modal's body.
         *
         * `headerText` is a string of text that will be displayed in the modal's header.
         *
         * `okOnly` is a boolean to indicate that the modal should only have an action button.
         *
         * `closeButtonText` text to be displayed in the close button for the modal. Default is "Cancel"
         *
         * `actionButtonText` text to be displayed in the action button for the modal. Default is "OK".
         *
         * @returns {Promise}  Returns a [$q](https://docs.angularjs.org/api/ng/service/$q) promise that resolves when the modal is closed
         * or is rejected when the modal is dismissed.
         * @memberof alkModalService
         */
        function show(customModalDefaults, customModalOptions) {
          if (!customModalDefaults) {
            customModalDefaults = {};
          }

          customModalDefaults.backdrop = customModalDefaults.backdrop || "static";

          //Create temp objects to work with since we"re in a singleton service
          var tempModalDefaults = {};
          var tempModalOptions = {};

          //Map angular-ui modal custom defaults to modal defaults defined in service
          angular.extend(tempModalDefaults, modalDefaults, customModalDefaults);

          //Map modal.html $scope custom properties to defaults defined in service
          angular.extend(tempModalOptions, getModalOptions(), customModalOptions);

          // prioritize template url over template
          if (tempModalDefaults.template && tempModalDefaults.templateUrl) {
            delete tempModalDefaults.template;
          }

          if (!tempModalDefaults.controller) {
            //tempModalDefaults.controller = controllerId; //TODO: what is this used for? if anything? controllerId is not defined.
            tempModalDefaults.resolve = {
              modalOptions: function () {
                return tempModalOptions;
              }
            };
            tempModalDefaults.controller = defaultController;
            tempModalDefaults.controllerAs = "$ctrl";
          }

          return $uibModal.open(tempModalDefaults).result;
        }

        //See places.controller.js for an example of this function being used

        /** initiateModalWizard - Initiates a modal wizard given an array of custom modal objects. Will launch the modals in the order they are provided in the array.
         * @param {Object[]} modalArray - modalArray. Each member of the array is an object with the following format:
         * `{onModalSuccess: function, modalOptions: object}`. The `modalOptions` object should be an object with standard [uib-modal](https://angular-ui.github.io/bootstrap/#!#modal) options.
         * `onModalSuccess` is a function that runs when the modal has been submitted. This can call [`openPrevModal`](alk.modal.alkModalService.html#openPrevModal) (go to prev modal in wizard)
         * or [`openNextModal`](alk.modal.alkModalService.html#openNextModal) (go to next modal in wizard).
         * @param {Object} wizardData - Object that contains fields that should persist for the duration of the modal wizard.
         * @memberof alkModalService
         */
        function initiateModalWizard(modalArray, wizardData) {
          currentWizardData = wizardData;
          modalWizardArray = modalArray;
          modalWizardIndex = 0;
          _openModal(modalWizardArray[0]);
        }

        /** openNextModal - Opens the next modal in the wizard. This should be called from within the
         * `onModalSuccess` callback function.
         * @memberof alkModalService
         * @param {Number} skip - optional arg to increment index
         */
        function openNextModal(skip = 0) {
          const nextModalWizardIndex = modalWizardIndex + skip + 1;
          if (nextModalWizardIndex >= modalWizardArray.length) {
            return;
          }
          const nextModal = modalWizardArray[nextModalWizardIndex];
          modalWizardIndex = nextModalWizardIndex;
          _openModal(nextModal);
        }

        /** openPrevModal - Opens the previous modal in the wizard. This should be called from within the modal
         * object's `onModalCancel` callback.
         * @memberof alkModalService
         * @param {Number} skip - optional arg to decrement index
         */
        function openPrevModal(skip = 0) {
          const prevModalWizardIndex = modalWizardIndex - skip - 1;
          if (prevModalWizardIndex < 0) {
            return;
          }
          modalWizardIndex = prevModalWizardIndex;
          _openModal(modalWizardArray[prevModalWizardIndex]);
        }

        /** getCurrentWizardData - Returns the current wizard data for the modal service.
         * @returns {Object} Object that contains fields that should persist for the duration of the modal wizard.
         * @memberof alkModalService
         */
        function getCurrentWizardData() {
          return currentWizardData;
        }

        /** setCurrentWizardData - Sets the current wizard data for the modal service.
         * @param {Object} data - Object that contains fields that should persist for the duration of the modal wizard.
         * @memberof alkModalService
         */
        function setCurrentWizardData(data) {
          currentWizardData = data;
        }

        //Clear the wizard data, which can also call a custom cleanup function that can be set via setWizardCleanupFunction().
        //To be called by consuming code when the modal completes successfully. (It gets called automatically called if the modal was cancelled.)
        function clearWizardData() {
          currentWizardData = null;
          modalWizardArray = [];
          modalWizardIndex = 0;
          if (wizardCleanupFunction) {
            wizardCleanupFunction();
            wizardCleanupFunction = null;
          }
        }

        function setWizardCleanupFunction(func) {
          wizardCleanupFunction = func;
        }

        //Used internally by modal wizard system
        function _openModal(customModalObj) {
          var modal = $uibModal.open(customModalObj.modalOptions);
          modal.result.then(function (result) {
            customModalObj.onModalSuccess(result);
          }, function (cancel) {
            clearWizardData();
          });
        }
      }
    ]);

})(window.angular);



// WEBPACK FOOTER //
// ./~/@trimblemaps/webtools-common/javascript/AngularJS/alk-modal/alkModalService.js